import { Box, Grid, useMediaQuery } from '@mui/material'
import { useMemo, useState } from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import InputCustom from '../../components/atoms/input/InputCustom'
import styles from './notificationV2.module.scss'
import riskAlertStyles from './riskAlert.module.scss'

import { getTimePassed } from '../../util/getTime'
import { getNotificationIcon, getNotificationIconType, isRiskAlertNotification } from '../../util/notificationIconHelper'
import { NotificationV2 } from './NotificationV2'
import { RiskAlertCard } from './RiskAlertCard'

const startOfDay = (d) => {
	const x = new Date(d)
	x.setHours(0, 0, 0, 0)
	return x
}

const isSameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime()

const subDays = (d, n) => {
	const x = new Date(d)
	x.setDate(x.getDate() - n)
	return x
}

const uiFromNotification = (n) => {
	return {
		icon: getNotificationIcon(n),
		iconType: getNotificationIconType(n),
		heading: n?.type || 'Notification',
		subheading: n?.message || '',
		time: getTimePassed(n?.createdAt),
		raw: n,
		paymentSuccess: getNotificationIconType(n) === 'payment',
	}
}

const typeMatchesTab = (type, tabIndex) => {
	const t = (type || '').toLowerCase()
	// Tab 0: All Notifications, Tab 1: Risk Alerts, Tab 2: Proposals, Tab 3: Payments, Tab 4: Chat
	if (tabIndex === 2) return t.includes('proposal')
	if (tabIndex === 3) return t.includes('payment') || t.includes('paid')
	if (tabIndex === 4) return t.includes('chat') || t.includes('message')
	return true
}

export const NotificationTab = ({ notifications }) => {
	const isSmallScreen = useMediaQuery('(max-width:991px)')

	const [search, setSearch] = useState('')
	const [value, setValue] = useState(0)

	const handleChange = (_e, newValue) => setValue(newValue)

	function CustomTabPanel(props) {
		const { children, value, index, ...other } = props
		return (
			<div
				role="tabpanel"
				hidden={value !== index}
				id={`simple-tabpanel-${index}`}
				aria-labelledby={`simple-tab-${index}`}
				{...other}
			>
				{value === index && <Box>{children}</Box>}
			</div>
		)
	}

	const mainTabStyle = {
		'& .MuiTabs-scrollButtons.Mui-disabled': { opacity: '0.3' },
		'&.MuiTabs-root': { minHeight: 'unset' },
		'& .MuiButtonBase-root': {
			padding: '11px 16px',
			minHeight: 'unset',
			fontSize: '14px',
			fontFamily: 'Inter',
			fontWeight: '500',
			color: '#313131',
			textTransform: 'capitalize',
			'&.Mui-selected': {
				color: '#7CA6BD',
				'& svg path': { stroke: '#7CA6BD' },
			},
		},
		'& .MuiTabs-indicator': { backgroundColor: '#7CA6BD' },
		'@media (max-width: 599px)': {
			'& .MuiButtonBase-root': { padding: '8px' },
		},
	}

	const handleSearch = (e) => setSearch(e.target.value)
	const { regularNotifications, riskAlerts } = useMemo(() => {
		const regular = (notifications || []).filter(n => !isRiskAlertNotification(n))
		const alerts = (notifications || []).filter(n => isRiskAlertNotification(n))
		return { regularNotifications: regular, riskAlerts: alerts }
	}, [notifications])

	// Count unread risk alerts for badge
	const unreadRiskAlertsCount = useMemo(() => {
		return riskAlerts.filter(a => a?.status === 'unread').length
	}, [riskAlerts])

	const uiRows = useMemo(() => {
		// All tabs except Risk Alerts (index 1) use regularNotifications (excludes risk_alert category)
		const rows = (regularNotifications || []).map(uiFromNotification)

		const filteredByTab = rows.filter((r) =>
			typeMatchesTab(r?.raw?.type, value)
		)

		const q = search.trim().toLowerCase()
		const filteredBySearch = q
			? filteredByTab.filter(
				(r) =>
					r.heading.toLowerCase().includes(q) ||
					(r.subheading || '').toLowerCase().includes(q)
			)
			: filteredByTab

		const today = new Date()
		const yesterday = subDays(today, 1)

		const todayRows = filteredBySearch.filter((r) =>
			r?.raw?.createdAt
				? isSameDay(new Date(r.raw.createdAt), today)
				: false
		)

		const yesterdayRows = filteredBySearch.filter((r) =>
			r?.raw?.createdAt
				? isSameDay(new Date(r.raw.createdAt), yesterday)
				: false
		)

		const olderRows = filteredBySearch.filter((r) => {
			if (!r?.raw?.createdAt) return false
			const d = new Date(r.raw.createdAt)
			return !isSameDay(d, today) && !isSameDay(d, yesterday)
		})

		return { todayRows, yesterdayRows, olderRows }
	}, [regularNotifications, value, search])

	// Group risk alerts by date (Today/Yesterday/Earlier) and sort descending
	const groupedRiskAlerts = useMemo(() => {
		const q = search.trim().toLowerCase()
		const filtered = q
			? riskAlerts.filter(a =>
				(a?.type || '').toLowerCase().includes(q) ||
				(a?.message || '').toLowerCase().includes(q)
			)
			: riskAlerts

		// Sort by createdAt descending (newest first)
		const sorted = [...filtered].sort((a, b) =>
			new Date(b?.createdAt) - new Date(a?.createdAt)
		)

		const today = new Date()
		const yesterday = subDays(today, 1)

		const todayAlerts = sorted.filter(a =>
			a?.createdAt ? isSameDay(new Date(a.createdAt), today) : false
		)
		const yesterdayAlerts = sorted.filter(a =>
			a?.createdAt ? isSameDay(new Date(a.createdAt), yesterday) : false
		)
		const earlierAlerts = sorted.filter(a => {
			if (!a?.createdAt) return false
			const d = new Date(a.createdAt)
			return !isSameDay(d, today) && !isSameDay(d, yesterday)
		})

		return { today: todayAlerts, yesterday: yesterdayAlerts, earlier: earlierAlerts, all: sorted }
	}, [riskAlerts, search])

	return (
		<>
			<section className={styles.notificationTabSection}>
				<Box sx={{ width: '100%' }} className={styles.notificationTabs}>
					<Box sx={{ borderBottom: 1, borderColor: '#DFE4EB' }}>
						<Tabs
							value={value}
							onChange={handleChange}
							aria-label="notification tabs"
							sx={mainTabStyle}
							variant={isSmallScreen ? 'scrollable' : 'standard'}
							scrollButtons={isSmallScreen ? 'auto' : false}
							allowScrollButtonsMobile={isSmallScreen}
						>
							<Tab
								label={
									<div className={styles.tabItem}>
										<span>All Notifications</span>
									</div>
								}
							/>
							<Tab
								label={
									<div className={styles.tabItem}>
										<span>
											Risk Alerts
											{unreadRiskAlertsCount > 0 && (
												<span style={{
													marginLeft: '6px',
													backgroundColor: '#dc2626',
													color: '#fff',
													borderRadius: '10px',
													padding: '2px 8px',
													fontSize: '11px',
													fontWeight: '600'
												}}>
													{unreadRiskAlertsCount}
												</span>
											)}
										</span>
									</div>
								}
							/>
							<Tab
								label={
									<div className={styles.tabItem}>
										<span>Proposals</span>
									</div>
								}
							/>
							<Tab
								label={
									<div className={styles.tabItem}>
										<span>Payments</span>
									</div>
								}
							/>
							<Tab
								label={
									<div className={styles.tabItem}>
										<span>Chat</span>
									</div>
								}
							/>
						</Tabs>
					</Box>

					<div className={styles.searchNotification}>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, sm: 4, md: 2.4 }}>
								<div className={styles.searchContainer}>
									<InputCustom
										type="text"
										name="search"
										value={search}
										id="search"
										placeholder="Search Messages"
										error={false}
										searchIcon={true}
										onChange={handleSearch}
									/>
								</div>
							</Grid>
						</Grid>
					</div>

					<div className={styles.tabContentWrp}>
						{/* All Notifications Tab */}
						<CustomTabPanel
							value={value}
							index={0}
							className={styles.notificationTabContent}
						>
							<div className={styles.allNotification}>
								<h5 className={styles.notificationTimeTitle}>
									Today
								</h5>
								{
									!uiRows?.todayRows?.length ?
										<p>No new notifications ...</p>
										:
										<ul className={styles.notificationList}>
											{uiRows.todayRows.map((item, index) => (
												<NotificationV2
													item={item}
													index={index}
													key={item?.raw?._id || index}
												/>
											))}
										</ul>
								}
							</div>

							<div className={styles.allNotification}>
								<h5 className={styles.notificationTimeTitle}>
									Yesterday
								</h5>
								{
									!uiRows?.yesterdayRows?.length ?
										<p>No notifications ...</p>
										:
										<ul className={styles.notificationList}>
											{uiRows.yesterdayRows.map((item, index) => (
												<NotificationV2
													item={item}
													index={index}
													key={item?.raw?._id || index}
												/>
											))}
										</ul>
								}
							</div>

							{uiRows?.olderRows?.length > 0 && (
								<div className={styles.allNotification}>
									<h5 className={styles.notificationTimeTitle}>
										Older
									</h5>
									<ul className={styles.notificationList}>
										{uiRows.olderRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								</div>
							)}
						</CustomTabPanel>

						{/* Risk Alerts Tab */}
						<CustomTabPanel
							value={value}
							index={1}
							className={styles.notificationTabContent}
						>
							{groupedRiskAlerts.all.length === 0 ? (
								<div className={riskAlertStyles.emptyRiskAlerts}>
									<p className={riskAlertStyles.emptyTitle}>No Risk Alerts</p>
									<p className={riskAlertStyles.emptyText}>
										You're all caught up! No action items require your attention.
									</p>
								</div>
							) : (
								<>
									{groupedRiskAlerts.today.length > 0 && (
										<div className={styles.allNotification}>
											<h5 className={styles.notificationTimeTitle}>
												Today
											</h5>
											<ul className={riskAlertStyles.riskAlertsList}>
												{groupedRiskAlerts.today.map((alert, index) => (
													<RiskAlertCard
														notification={alert}
														index={index}
														key={alert?._id || index}
													/>
												))}
											</ul>
										</div>
									)}

									{groupedRiskAlerts.yesterday.length > 0 && (
										<div className={styles.allNotification}>
											<h5 className={styles.notificationTimeTitle}>
												Yesterday
											</h5>
											<ul className={riskAlertStyles.riskAlertsList}>
												{groupedRiskAlerts.yesterday.map((alert, index) => (
													<RiskAlertCard
														notification={alert}
														index={index}
														key={alert?._id || index}
													/>
												))}
											</ul>
										</div>
									)}

									{groupedRiskAlerts.earlier.length > 0 && (
										<div className={styles.allNotification}>
											<h5 className={styles.notificationTimeTitle}>
												Earlier
											</h5>
											<ul className={riskAlertStyles.riskAlertsList}>
												{groupedRiskAlerts.earlier.map((alert, index) => (
													<RiskAlertCard
														notification={alert}
														index={index}
														key={alert?._id || index}
													/>
												))}
											</ul>
										</div>
									)}
								</>
							)}
						</CustomTabPanel>

						{/* Proposals Tab */}
						<CustomTabPanel
							value={value}
							index={2}
							className={styles.notificationTabContent}
						>
							<div className={styles.allNotification}>
								<h5 className={styles.notificationTimeTitle}>
									Today
								</h5>
								{!uiRows?.todayRows?.length ? (
									<p>No proposal notifications today...</p>
								) : (
									<ul className={styles.notificationList}>
										{uiRows.todayRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								)}
							</div>
							{uiRows?.yesterdayRows?.length > 0 && (
								<div className={styles.allNotification}>
									<h5 className={styles.notificationTimeTitle}>Yesterday</h5>
									<ul className={styles.notificationList}>
										{uiRows.yesterdayRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								</div>
							)}
							{uiRows?.olderRows?.length > 0 && (
								<div className={styles.allNotification}>
									<h5 className={styles.notificationTimeTitle}>Older</h5>
									<ul className={styles.notificationList}>
										{uiRows.olderRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								</div>
							)}
						</CustomTabPanel>

						{/* Payments Tab */}
						<CustomTabPanel
							value={value}
							index={3}
							className={styles.notificationTabContent}
						>
							<div className={styles.allNotification}>
								<h5 className={styles.notificationTimeTitle}>
									Today
								</h5>
								{!uiRows?.todayRows?.length ? (
									<p>No payment notifications today...</p>
								) : (
									<ul className={styles.notificationList}>
										{uiRows.todayRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								)}
							</div>
							{uiRows?.yesterdayRows?.length > 0 && (
								<div className={styles.allNotification}>
									<h5 className={styles.notificationTimeTitle}>Yesterday</h5>
									<ul className={styles.notificationList}>
										{uiRows.yesterdayRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								</div>
							)}
							{uiRows?.olderRows?.length > 0 && (
								<div className={styles.allNotification}>
									<h5 className={styles.notificationTimeTitle}>Older</h5>
									<ul className={styles.notificationList}>
										{uiRows.olderRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								</div>
							)}
						</CustomTabPanel>

						{/* Chat Tab */}
						<CustomTabPanel
							value={value}
							index={4}
							className={styles.notificationTabContent}
						>
							<div className={styles.allNotification}>
								<h5 className={styles.notificationTimeTitle}>
									Today
								</h5>
								{!uiRows?.todayRows?.length ? (
									<p>No chat notifications today...</p>
								) : (
									<ul className={styles.notificationList}>
										{uiRows.todayRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								)}
							</div>
							{uiRows?.yesterdayRows?.length > 0 && (
								<div className={styles.allNotification}>
									<h5 className={styles.notificationTimeTitle}>Yesterday</h5>
									<ul className={styles.notificationList}>
										{uiRows.yesterdayRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								</div>
							)}
							{uiRows?.olderRows?.length > 0 && (
								<div className={styles.allNotification}>
									<h5 className={styles.notificationTimeTitle}>Older</h5>
									<ul className={styles.notificationList}>
										{uiRows.olderRows.map((item, index) => (
											<NotificationV2
												item={item}
												index={index}
												key={item?.raw?._id || index}
											/>
										))}
									</ul>
								</div>
							)}
						</CustomTabPanel>
					</div>
				</Box>
			</section>
		</>
	)

}
